import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, FlaskConical, Info, Loader2, Trash2 } from 'lucide-react';
import { clsx } from 'clsx';
import {
  Button,
  Callout,
  ConfirmDialog,
  EmptyState,
  ProgressBar,
  Spinner,
  useToast,
} from '@/shared/ui';
import patterns from '@/shared/ui/styles/patterns.module.css';
import { useVerificationDetail } from '../hooks/useVerificationDetail';
import { useVerificationActions } from '../hooks/useVerificationActions';
import { useConnections } from '../hooks/useConnections';
import { useCanDelete } from '../hooks/useCanDelete';
import { useCanEdit } from '../hooks/useCanEdit';
import { IV_BASE, IvSubnav } from '../components/IvSubnav';
import { BenefitBreakdownTab } from '../components/BenefitBreakdownTab';
import { StillNeededTab } from '../components/StillNeededTab';
import { AuditTrailTab } from '../components/AuditTrailTab';
import { RevealDob } from '../components/RevealDob';
import {
  formatDateTimeInline,
  getErrorMessage,
  isRunning,
  pluralize,
  relationshipLabel,
} from '../utils/format';
import styles from './VerificationDetailPage.module.css';

type Tab = 'benefits' | 'needed' | 'audit';

/** Remounts the page per verification so tab, dialogs and a revealed DOB never
 *  carry over from one patient to the next. */
export function VerificationDetailPage() {
  const { id = '' } = useParams<{ id: string }>();
  return <VerificationDetailPageFor key={id} id={id} />;
}

function VerificationDetailPageFor({ id }: { id: string }) {
  const navigate = useNavigate();
  const toast = useToast();
  const [tab, setTab] = useState<Tab>('benefits');
  const [confirmApprove, setConfirmApprove] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const { detail, isLoading, isError, error, refetch } = useVerificationDetail(id);
  const { approve, flag, queueCall, rerun, remove } = useVerificationActions(id);
  const { connections } = useConnections();
  const canDelete = useCanDelete();
  const canEdit = useCanEdit();

  if (isLoading && !detail) {
    return (
      <div className={patterns.page}>
        <IvSubnav />
        <div className={styles.loading}>
          <Spinner />
        </div>
      </div>
    );
  }

  if (isError || !detail) {
    return (
      <div className={patterns.page}>
        <IvSubnav />
        <Link to={IV_BASE} className={styles.back}>
          <ArrowLeft size={14} aria-hidden /> Back to worklist
        </Link>
        <div className={patterns.panel}>
          <EmptyState
            title="Couldn't load this verification"
            description={getErrorMessage(
              error,
              'It may have been removed, or the server is unavailable.',
            )}
            action={{ label: 'Retry', onClick: () => refetch() }}
          />
        </div>
      </div>
    );
  }

  const { verification: v, row, completeness, audit, rawResponses, benefitWrites } = detail;
  const patientName = `${v.patient.firstName} ${v.patient.lastName}`.trim();
  const unmetRequired = completeness?.unmetRequired.length ?? 0;
  const met = completeness?.metCount ?? row.completeness?.met ?? 0;
  const total = completeness?.total ?? row.completeness?.total ?? 0;
  const running = isRunning(v.status);
  const canApprove = v.status === 'needs_review' || v.status === 'complete';
  const dryRun = connections?.openDental.writeMode === 'dry_run';

  const appointmentLine = [
    v.appointment.typeLabel,
    v.appointment.at ? formatDateTimeInline(v.appointment.at) : null,
    v.appointment.providerName,
  ]
    .filter(Boolean)
    .join(' · ');

  const planParts = [
    v.plan.carrierName,
    v.plan.planType.value,
    `Member #${row.memberIdMasked}`,
    v.plan.groupName ? `Group ${v.plan.groupName}` : null,
    `Subscriber (${relationshipLabel(v.subscriber.relationshipToPatient)})`,
  ].filter(Boolean);

  const originLine =
    v.intakeSource === 'open_dental_sync'
      ? `Synced from Open Dental${v.pms?.aptNum ? ` · appt ${v.pms.aptNum}` : ''}`
      : v.intakeSource === 'file_import'
        ? `Imported from schedule${v.importBatchId ? ` · batch ${v.importBatchId}` : ''}`
        : 'Added manually';

  function handleFlag() {
    flag.mutate(undefined, {
      onSuccess: () => toast.success('Flagged for a second reviewer'),
      onError: (err) => toast.error(getErrorMessage(err, "Couldn't flag this verification")),
    });
  }

  function handleQueueCall() {
    queueCall.mutate(undefined, {
      onSuccess: (r) =>
        toast.success(
          `Payer call queued — ${pluralize(r.questions.length, 'question')} will be asked`,
        ),
      onError: (err) => toast.error(getErrorMessage(err, "Couldn't queue the payer call")),
    });
  }

  function handleRerun() {
    rerun.mutate(undefined, {
      onSuccess: (r) =>
        r.queued
          ? toast.success('Electronic check re-queued')
          : toast.warning(r.reason ?? "The check couldn't be re-queued"),
      onError: (err) => toast.error(getErrorMessage(err, "Couldn't re-run the check")),
    });
  }

  function handleDelete() {
    remove.mutate(undefined, {
      onSuccess: () => {
        setConfirmDelete(false);
        toast.success('Verification deleted');
        navigate(IV_BASE);
      },
      onError: (err) => {
        setConfirmDelete(false);
        toast.error(getErrorMessage(err, "Couldn't delete this verification"));
      },
    });
  }

  function handleApprove() {
    approve.mutate(undefined, {
      onSuccess: (r) => {
        setConfirmApprove(false);
        toast.success(r.message);
      },
      onError: (err) => {
        setConfirmApprove(false);
        toast.error(getErrorMessage(err, 'Approval failed'));
      },
    });
  }

  return (
    <div className={patterns.page}>
      <IvSubnav />

      <Link to={IV_BASE} className={styles.back}>
        <ArrowLeft size={14} aria-hidden /> Back to worklist
      </Link>

      <header className={clsx(patterns.panel, styles.head)}>
        <div className={styles.who}>
          <h1 className={styles.name}>{patientName || row.patientName}</h1>
          <div className={styles.meta}>
            DOB <RevealDob verificationId={v.id} masked={v.patient.dateOfBirthMasked} />
          </div>
          <div className={styles.meta}>{appointmentLine}</div>
          <div className={styles.meta}>{planParts.join(' · ')}</div>
          <div className={clsx(styles.meta, styles.metaMuted)}>{originLine}</div>
        </div>
        <div className={styles.completeness}>
          <div className={styles.compTop}>
            <span>Completeness</span>
            <span>
              <strong>
                {met} / {total}
              </strong>{' '}
              required
            </span>
          </div>
          <ProgressBar value={met} max={total || 1} height={8} />
          {completeness ? (
            unmetRequired > 0 ? (
              <div className={clsx(styles.compNote, styles.compNoteWarning)}>
                {pluralize(
                  unmetRequired,
                  'required detail still needs',
                  'required details still need',
                )}{' '}
                a payer call
              </div>
            ) : (
              <div className={clsx(styles.compNote, styles.compNoteSuccess)}>
                All required details verified
              </div>
            )
          ) : (
            <div className={clsx(styles.compNote, styles.compNoteMuted)}>Not evaluated yet</div>
          )}
        </div>
      </header>

      {running && (
        <Callout variant="info" icon={Loader2}>
          Electronic check is running… this page refreshes on its own.
        </Callout>
      )}
      {v.status === 'needs_review' && (
        <Callout variant="accent" icon={Info}>
          Electronic check returned coverage, maximums and category percentages. Frequencies aren't
          carried on a 271 — those are queued for later tiers.
        </Callout>
      )}
      {v.status === 'failed' && (
        <Callout
          variant="error"
          title="Electronic check failed"
          actions={
            canEdit ? (
              <Button variant="secondary" size="sm" onClick={handleRerun} isLoading={rerun.isPending}>
                Re-run check
              </Button>
            ) : undefined
          }
        >
          {row.failureMessage ??
            'The payer did not return a match. Check the member ID and date of birth, then re-run.'}
        </Callout>
      )}
      {v.status === 'awaiting_production' && (
        <Callout variant="purple" icon={FlaskConical}>
          Stedi is in test mode and this carrier has no mock payer. The check will run on its own
          once Stedi is switched to production on the Connections page.
        </Callout>
      )}

      <div className={styles.tabs} role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'benefits'}
          className={clsx(styles.tab, tab === 'benefits' && styles.activeTab)}
          onClick={() => setTab('benefits')}
        >
          Benefit breakdown
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'needed'}
          className={clsx(styles.tab, tab === 'needed' && styles.activeTab)}
          onClick={() => setTab('needed')}
        >
          Still needed{' '}
          <span className={unmetRequired > 0 ? styles.tabCountWarning : styles.tabCount}>
            ({unmetRequired})
          </span>
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'audit'}
          className={clsx(styles.tab, tab === 'audit' && styles.activeTab)}
          onClick={() => setTab('audit')}
        >
          Audit trail
        </button>
      </div>

      <div role="tabpanel">
        {tab === 'benefits' && <BenefitBreakdownTab verification={v} />}
        {tab === 'needed' && (
          <StillNeededTab
            completeness={completeness}
            onQueueCall={handleQueueCall}
            queueing={queueCall.isPending}
          />
        )}
        {tab === 'audit' && (
          <AuditTrailTab
            verification={v}
            audit={audit}
            rawResponses={rawResponses}
            benefitWrites={benefitWrites}
          />
        )}
      </div>

      <footer className={styles.footer}>
        {canDelete && (
          <Button
            variant="ghost"
            className={styles.deleteBtn}
            leftIcon={<Trash2 size={14} aria-hidden />}
            onClick={() => setConfirmDelete(true)}
          >
            Delete
          </Button>
        )}
        {canEdit && (
          <>
            <Button variant="ghost" onClick={handleFlag} isLoading={flag.isPending}>
              Flag for review
            </Button>
            <Button variant="secondary" onClick={handleQueueCall} isLoading={queueCall.isPending}>
              Queue payer call
            </Button>
            <Button
              onClick={() => setConfirmApprove(true)}
              disabled={!canApprove}
              title={canApprove ? undefined : 'Available once the electronic check needs review'}
            >
              Approve &amp; write to Open Dental
            </Button>
          </>
        )}
      </footer>

      <ConfirmDialog
        isOpen={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        onConfirm={handleDelete}
        title="Delete this verification?"
        message={
          <>
            Removes this appointment from the worklist along with its audit trail and payer
            responses. This can't be undone.
            {v.intakeSource === 'open_dental_sync' && (
              <> If the appointment is still in Open Dental, the next sync will bring it back.</>
            )}
            {benefitWrites.some((w) => w.mode === 'live') && (
              <>
                <br />
                <br />
                <em>Benefits were already written to Open Dental, so this row can't be deleted.</em>
              </>
            )}
          </>
        }
        confirmLabel="Delete"
        variant="destructive"
        isLoading={remove.isPending}
        confirmDisabled={benefitWrites.some((w) => w.mode === 'live')}
      />

      <ConfirmDialog
        isOpen={confirmApprove}
        onClose={() => setConfirmApprove(false)}
        onConfirm={handleApprove}
        title="Approve this verification?"
        message={
          <>
            Marks every confident field as reviewed and writes them to Open Dental.
            {unmetRequired > 0 && (
              <>
                {' '}
                {pluralize(unmetRequired, 'required detail is', 'required details are')} still
                missing and will be held back.
              </>
            )}
            {dryRun && (
              <>
                <br />
                <br />
                <em>(dry run — nothing will change in Open Dental)</em>
              </>
            )}
          </>
        }
        confirmLabel={dryRun ? 'Approve (dry run)' : 'Approve & write'}
        isLoading={approve.isPending}
      />
    </div>
  );
}
